import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Plus } from 'lucide-react';
import { Button } from '@headlessui/react';
import toast from 'react-hot-toast';
import SearchCreateSelect, {
  SearchCreateOption,
} from 'components/common/SearchCreateSelect';
import UserAvatar from 'components/common/UserAvatar';
import { contactDisplayName } from 'utils/crmContact';
import CompaniesModal from 'components/Modals/CompaniesModal';
import { CompanyFormValues, CompanyRowData } from 'types/crm/company';
import {
  useCreateDealCompanyMutation,
  useNonAssociatedDealCompanies,
  useNonAssociatedDealContacts,
  useUpdateDealAssociationsMutation,
} from 'service/crm';
import StatCard, { Stat } from 'components/common/StatCard';
import SectionHeader from 'components/common/SectionHeader';
import { BuildingIcon, UserIcon } from 'assets/Icons';
import AssociationEmptyState from '../AssociationEmptyState';
import ContactRow from '../ContactRow';
import CompanyRow from '../CompanyRow';
import { Timestamp } from '../Contacts/ContactAssociation';
import Timestamps from '../Timestamps';
import ContactOptionRow, { AssociationOption } from '../ContactOptionRow';
import { useRecordAccess } from 'context/crm/RecordAccessContext';

export type AssociatedContact = {
  uuid: string;
  /** Human-readable reference (e.g. "CT003") — what the contact URL is built from. */
  contactKey?: string;
  name: string;
  email?: string;
  profileImageUrl?: string | null;
};

type DealAssociationProps = {
  dealUuid: string;
  stats?: Stat[];
  contacts?: AssociatedContact[];
  companies?: CompanyRowData[];
  isLoadingContacts?: boolean;
  isLoadingCompanies?: boolean;
  onAddContact?: () => void;
  onAddCompany?: () => void;
  timestamps?: Timestamp[];
};

const EmptyRow = ({ children }: { children: React.ReactNode }) => (
  <div className="px-1 py-2.5 text-[13px] text-neutral-faded">{children}</div>
);



const SEARCH_LIMIT = 20;


const DealAssociation = ({
  dealUuid,
  stats = [],
  contacts = [],
  companies = [],
  isLoadingContacts = false,
  isLoadingCompanies = false,
  onAddContact,
  onAddCompany,
  timestamps = [],
}: DealAssociationProps) => {
  const [showAllContacts, setShowAllContacts] = useState(false);
  const [showAllCompanies, setShowAllCompanies] = useState(false);
  const [showContactSearch, setShowContactSearch] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [showCompanySearch, setShowCompanySearch] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');

  /**
   * RBAC (sections 6, 13, 19): associating a contact or company are modification
   * actions on THIS deal, so they ride on this deal's access — not on the
   * associated record's own canEdit (sections 20, 21). Viewing the lists,
   * "View all" and clicking through stay open to everyone.
   */
  const { canManageAssociations } = useRecordAccess();

  const createCompanyMutation = useCreateDealCompanyMutation();
  const updateAssociationsMutation = useUpdateDealAssociationsMutation();

  const {
    contacts: nonAssociatedContacts,
    isLoading: isSearchingContacts,
    isFetchingMore: isFetchingMoreContacts,
    hasMore: hasMoreContacts,
    onLoadMore: loadMoreContacts,
  } = useNonAssociatedDealContacts(
    {
      dealUuid,
      search: contactSearchTerm,
      limit: SEARCH_LIMIT,
    },
    // Never run the non-associated lookup for a view-only user — it only feeds a
    // picker they can't open.
    { enabled: showContactSearch && canManageAssociations },
  );

  const {
    companies: nonAssociatedCompanies,
    isLoading: isSearchingCompanies,
    isFetchingMore: isFetchingMoreCompanies,
    hasMore: hasMoreCompanies,
    onLoadMore: loadMoreCompanies,
  } = useNonAssociatedDealCompanies(
    {
      dealUuid,
      search: companySearchTerm,
      limit: SEARCH_LIMIT,
    },
    { enabled: showCompanySearch && canManageAssociations },
  );

  const contactOptions = useMemo<AssociationOption[]>(
    () =>
      nonAssociatedContacts.map((c) => ({
        uuid: c.uuid,
        reference: c.contactKey,
        name: contactDisplayName(c),
        imageUrl: null,
      })),
    [nonAssociatedContacts],
  );

  const companyOptions = useMemo<AssociationOption[]>(
    () =>
      nonAssociatedCompanies.map((c) => ({
        uuid: c.uuid,
        reference: c.companyKey,
        name: c.name,
        subtitle: [c.industry, c.cityLocation].filter(Boolean).join(' · '),
        imageUrl: c.imageUrl || null,
      })),
    [nonAssociatedCompanies],
  );

  // Section 23: access can disappear mid-session. Collapse anything open.
  useEffect(() => {
    if (!canManageAssociations) {
      setShowContactSearch(false);
      setContactSearchTerm('');
      setShowCompanySearch(false);
      setCompanySearchTerm('');
    }
  }, [canManageAssociations]);

  const handleToggleContactSearch = () => {
    if (!canManageAssociations) return;
    setShowContactSearch((prev) => !prev);
    setContactSearchTerm('');
    onAddContact?.();
  };


  const associate = (
    option: AssociationOption,
    entity: 'contact' | 'company',
  ) => {
    if (!canManageAssociations) {
      toast.error('You do not have permission to change associations on this deal.');
      return;
    }
    if (!option.reference) {
      toast.error(
        `Can't add ${option.name} — this ${entity} has no reference key.`,
      );
      return;
    }

    updateAssociationsMutation.mutate(
      {
        dealUuid,
        ...(entity === 'contact'
          ? { contactReferences: [option.reference] }
          : { companyReferences: [option.reference] }),
      },
      {
        onSuccess: () =>
          toast.success(
            entity === 'contact'
              ? 'Contact added to deal'
              : 'Company added to deal',
          ),
        onError: (err: any) =>
          toast.error(
            err?.response?.data?.message ??
            err?.message ??
            `Failed to add ${entity}. Please try again.`,
          ),
      },
    );
  };

  const handleToggleCompanySearch = () => {
    if (!canManageAssociations) return;
    setShowCompanySearch((prev) => !prev);
    setCompanySearchTerm('');
    onAddCompany?.();
  };

  const handleCreateCompany = async (values: CompanyFormValues) => {
    if (!canManageAssociations) {
      throw new Error('You do not have permission to change associations on this deal.');
    }
    await createCompanyMutation.mutateAsync({
      dealUuid,
      companyName: values.title,
      industry: values.industry || undefined,
      cityLocation: values.city || undefined,
      websiteUrl: values.website || undefined,
      phone: values.phone || undefined,
      appUserUuid: values.companyOwnerUuid || undefined,
      linkedInUrl: values.linkedin || undefined,
      tags: values.tags,
      description: values.description || undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const visibleContacts = showAllContacts ? contacts : contacts.slice(0, 3);
  const visibleCompanies = showAllCompanies ? companies : companies.slice(0, 3);

  return (
    <div className="w-full min-h-full rounded-r-xl bg-primary border-l border-primary-subtle">
      {/* Stats */}
      {stats.length > 0 && (
        <div className="grid grid-cols-2 gap-3 p-5 border-b border-primary-subtle">
          {stats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </div>
      )}

      {/* Associated contacts */}
      <div className='p-5 border-b border-primary-subtle'>
        <SectionHeader
          title="ASSOCIATED CONTACTS"
          onAdd={canManageAssociations ? handleToggleContactSearch : undefined}
        />
        {showContactSearch && canManageAssociations && (
          <div className="mb-3">
            <SearchCreateSelect
              placeholder="Search to add contacts..."
              listHeading="Existing Contacts"
              emptyLabel="No contacts found"
              value={contactSearchTerm}
              options={contactOptions}
              isLoading={isSearchingContacts}
              isFetchingMore={isFetchingMoreContacts}
              hasMore={hasMoreContacts}
              onLoadMore={loadMoreContacts}
              hideCreateButton
              clearOnSelect
              renderOption={(option, select) => (
                <ContactOptionRow option={option} onSelect={select} />
              )}
              onSearchChange={setContactSearchTerm}
              onSelectOption={(option) => {
                associate(option, 'contact');
                setContactSearchTerm('');
              }}
            />
          </div>
        )}
        <div className={`space-y-3 ${showAllContacts ? 'max-h-[400px] overflow-y-auto pr-1' : ''}`}>
          {visibleContacts.map((contact) => (
            <ContactRow key={contact.uuid} contact={contact} />
          ))}
          {contacts.length === 0 &&
            (isLoadingContacts ? (
              <EmptyRow>Loading contacts…</EmptyRow>
            ) : (
              <AssociationEmptyState
                icon={<UserIcon size={20} color="#8B5CF6" />}
                title="No contact linked"
                description="Associate a contact to track who's involved in this deal."
                actionLabel={canManageAssociations ? 'Add contact' : undefined}
                onAction={canManageAssociations ? handleToggleContactSearch : undefined}
              />
            ))}
        </div>
        {contacts.length > 3 && (
          <Button
            onClick={() => setShowAllContacts((prev) => !prev)}
            className="mt-3 w-full rounded-xl border border-primary-subtle py-2.5 text-center text-[14px] font-medium text-neutral-faded data-active:opacity-70"
          >
            {showAllContacts ? 'Show less' : 'View all'}
          </Button>
        )}
      </div>

      {/* Companies */}
      <div className='p-5 '>
        <SectionHeader
          title="COMPANIES"
          onAdd={canManageAssociations ? handleToggleCompanySearch : undefined}
        />
        {showCompanySearch && canManageAssociations && (
          <div className="mb-3">
            <SearchCreateSelect
              placeholder="Search to add companies..."
              listHeading="Existing Companies"
              emptyLabel="No companies found"
              createButtonLabel="Create New Company"
              icon={Building2}
              value={companySearchTerm}
              options={companyOptions}
              isLoading={isSearchingCompanies}
              isFetchingMore={isFetchingMoreCompanies}
              hasMore={hasMoreCompanies}
              onLoadMore={loadMoreCompanies}
              createDisplay="modal"
              showOptionAddAction
              clearOnSelect
              onSearchChange={setCompanySearchTerm}
              onSelectOption={(option) => {
                associate(option, 'company');
                setCompanySearchTerm('');
              }}
              renderCreateModal={({ open, onClose }) => (
                <CompaniesModal
                  isOpen={open}
                  onClose={onClose}
                  isSaving={createCompanyMutation.isPending}
                  onSubmit={async (values) => {
                    try {
                      await handleCreateCompany(values);
                      toast.success('Company created successfully');
                      onClose();
                    } catch (err: any) {
                      toast.error(
                        err?.response?.data?.message ??
                        err?.message ??
                        'Failed to create company. Please try again.',
                      );
                    }
                  }}
                />
              )}
            />
          </div>
        )}
        <div className={showAllCompanies ? 'max-h-80 overflow-y-auto pr-1' : undefined}>
          {visibleCompanies.map((company) => (
            <CompanyRow key={company.uuid} company={company} />
          ))}
          {companies.length === 0 &&
            (isLoadingCompanies ? (
              <EmptyRow>Loading companies…</EmptyRow>
            ) : (
              <AssociationEmptyState
                icon={<BuildingIcon size={20} />}
                title="No company linked"
                description="Associate a company to add shared context."
                actionLabel={canManageAssociations ? 'Add company' : undefined}
                onAction={canManageAssociations ? handleToggleCompanySearch : undefined}
              />
            ))}
        </div>
        {companies.length > 3 && (
          <Button
            onClick={() => setShowAllCompanies((prev) => !prev)}
            className="mt-3 w-full rounded-xl border border-primary-subtle py-2.5 text-center text-[14px] font-medium text-neutral-faded data-active:opacity-70"
          >
            {showAllCompanies ? 'Show less' : 'View all'}
          </Button>
        )}
      </div>

      {/* Timestamps */}
      {timestamps && timestamps.length > 0 && (
        <Timestamps
          className="p-5 border-t border-primary-subtle"
          timestamps={timestamps}
        />
      )}

    </div>
  );
};

export default DealAssociation;